import React from 'react';
// 1. Import motion for animations
import { motion } from 'framer-motion';

// 2. Define animation variants for the hero section
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const sentenceVariant = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.08,
    },
  },
};

const wordVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function PrivacyPolicy() {
  const navLinks = [
    { href: "#introduction", text: "Introduction" },
    { href: "#data-collection", text: "Data Collection" },
    { href: "#data-use", text: "Data Use" },
    { href: "#legal-basis", text: "Legal Basis" },
    { href: "#data-sharing", text: "Data Sharing" },
    { href: "#data-retention", text: "Data Retention" },
    { href: "#international-transfers", text: "International Transfers" },
    { href: "#your-rights", text: "Your Rights" },
    { href: "#data-security", text: "Data Security" },
    { href: "#cookies", text: "Cookies" },
    { href: "#childrens-privacy", text: "Children's Privacy" },
    { href: "#policy-updates", text: "Policy Updates" },
    { href: "#contact-us", text: "Contact Us" },
  ];

  return (
    <main className="bg-white text-gray-900 font-sans antialiased overflow-x-hidden  ">
      {/* Hero Section with Animations */}
      <section className="relative flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white h-[40vh] sm:h-[50vh]  pt-20 sm:pt-20">
        <div className="absolute inset-0 z-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1400 400" preserveAspectRatio="xMidYMid slice">
            <path fill="#16a085" d="M0,400L80,373.3C160,347,320,293,480,280C640,267,800,293,960,306.7C1120,320,1280,320,1360,320L1400,320L1400,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z" />
          </svg>
        </div>
        <motion.div 
          className="relative z-10 max-w-7xl mx-auto text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4"
            variants={sentenceVariant}
          >
            <span className="block">
              {"Privacy Policy".split(" ").map((word, i) => <motion.span key={i} className="inline-block" variants={wordVariant}>{word} </motion.span>)}
            </span>
            <span className="block text-[#16a085] mt-2">
              {"Your Privacy, Our Commitment".split(" ").map((word, i) => <motion.span key={i} className="inline-block" variants={wordVariant}>{word} </motion.span>)}
            </span>
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="mt-3 max-w-4xl mx-auto text-base sm:text-lg text-gray-700 leading-snug"
          >
            This policy governs the collection, processing, and protection of your personal data in accordance with applicable data protection laws, including the General Data Protection Regulation (GDPR).
          </motion.p>
        </motion.div>
      </section>

      {/* Policy Content (Static - No Animations) */}
      <div className="max-w-7xl xl:max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <aside className="lg:w-64 flex-shrink-0 lg:sticky lg:top-24 self-start">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Table of Contents
            </h2>
            <div className="lg:hidden flex overflow-x-auto scrollbar-hide -mx-4 px-4 pb-4 space-x-2">
                {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="block flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#16a085] bg-gray-50 hover:bg-gray-100 rounded-md transition-colors duration-150"
                    >
                      {link.text}
                    </a>
                ))}
            </div>
            <ul className="hidden lg:block space-y-2">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#16a085] hover:bg-gray-50 rounded-md transition-colors duration-150">
                      {link.text}
                    </a>
                  </li>
                ))}
            </ul>
          </aside>

          <div className="lg:flex-1 min-w-0">
            <div className="prose prose-lg max-w-none text-gray-700">
              <section id="introduction" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">1. Introduction</h2>
                <p>Void Support System ("Void", "we", "us", or "our") is committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform or use our services.</p>
                <p className="mt-4">We comply with the General Data Protection Regulation (GDPR) and other applicable data protection laws. By accessing or using our services, you agree to the terms of this Privacy Policy.</p>
              </section>

              <section id="data-collection" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">2. Data We Collect</h2>
                <p>We collect several types of information from and about users of our platform, including:</p>
                <div className="mt-6 space-y-6">
                  <div><h3 className="text-lg font-semibold text-gray-900">Personal Identifiers</h3><p className="mt-1">Name, email address, phone number, business details, and other contact information.</p></div>
                  <div><h3 className="text-lg font-semibold text-gray-900">Account Information</h3><p className="mt-1">Username, password, account preferences, and transaction history.</p></div>
                  <div><h3 className="text-lg font-semibold text-gray-900">Technical Data</h3><p className="mt-1">IP address, browser type, operating system, device information, and usage data.</p></div>
                  <div><h3 className="text-lg font-semibold text-gray-900">Cookies and Tracking</h3><p className="mt-1">We use cookies and similar tracking technologies to enhance your experience and analyze usage.</p></div>
                </div>
              </section>

              {/* ... (The rest of the static content sections remain unchanged) ... */}

              <section id="data-use" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">3. How We Use Your Data</h2>
                <p>We use the information we collect for various purposes, including:</p>
                <ul className="mt-4 space-y-3 list-disc list-inside">
                  <li>To provide and maintain our services</li>
                  <li>To notify you about changes to our services</li>
                  <li>To allow you to participate in interactive features</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information to improve our services</li>
                  <li>To monitor the usage of our services</li>
                  <li>To detect, prevent, and address technical issues</li>
                  <li>For compliance with legal obligations</li>
                </ul>
              </section>

              <section id="legal-basis" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">4. Legal Basis for Processing</h2>
                <p>We process your personal information under the following legal bases:</p>
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  <div className="p-4 bg-gray-50 rounded-lg"><h3 className="text-lg font-semibold text-[#16a085]">Consent</h3><p className="mt-2">When you've given us clear consent for specific processing activities.</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><h3 className="text-lg font-semibold text-[#16a085]">Contractual Necessity</h3><p className="mt-2">When processing is necessary for a contract with you.</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><h3 className="text-lg font-semibold text-[#16a085]">Legal Obligation</h3><p className="mt-2">When processing is required to comply with the law.</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><h3 className="text-lg font-semibold text-[#16a085]">Legitimate Interests</h3><p className="mt-2">When processing is necessary for our legitimate business interests.</p></div>
                </div>
              </section>

              <section id="data-sharing" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">5. Data Sharing and Disclosure</h2>
                <p>We may share your information in the following situations:</p>
                <div className="mt-6 space-y-6">
                  <div><h3 className="text-lg font-semibold text-gray-900">Service Providers</h3><p className="mt-1">With third-party vendors who perform services on our behalf (e.g., hosting, analytics, customer service).</p></div>
                  <div><h3 className="text-lg font-semibold text-gray-900">Business Transfers</h3><p className="mt-1">In connection with any merger, sale of company assets, or acquisition.</p></div>
                  <div><h3 className="text-lg font-semibold text-gray-900">Legal Requirements</h3><p className="mt-1">If required to do so by law or in response to valid requests by public authorities.</p></div>
                </div>
                <p className="mt-6 font-medium">We do not sell your personal information to third parties.</p>
              </section>
              
              <section id="data-retention" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">6. Data Retention</h2>
                <p>We retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.</p>
                <p className="mt-4">Retention periods vary depending on the type of data and the purpose for which it was collected. When we no longer need to process your personal information, we will delete or anonymize it.</p>
              </section>

              <section id="international-transfers" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">7. International Data Transfers</h2>
                <p>Your information, including personal data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.</p>
                <p className="mt-4">We ensure that appropriate safeguards are in place for such transfers, including standard contractual clauses approved by the European Commission or other applicable mechanisms.</p>
              </section>

              <section id="your-rights" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">8. Your Data Protection Rights</h2>
                <p>Depending on your location, you may have the following rights regarding your personal data:</p>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <div className="p-4 border border-gray-200 rounded-lg"><h3 className="font-semibold text-[#16a085]">Right to Access</h3><p className="mt-1 text-sm">Request copies of your personal data.</p></div>
                  <div className="p-4 border border-gray-200 rounded-lg"><h3 className="font-semibold text-[#16a085]">Right to Rectification</h3><p className="mt-1 text-sm">Request correction of inaccurate data.</p></div>
                  <div className="p-4 border border-gray-200 rounded-lg"><h3 className="font-semibold text-[#16a085]">Right to Erasure</h3><p className="mt-1 text-sm">Request deletion of your personal data.</p></div>
                  <div className="p-4 border border-gray-200 rounded-lg"><h3 className="font-semibold text-[#16a085]">Right to Restrict Processing</h3><p className="mt-1 text-sm">Request restriction of processing your data.</p></div>
                  <div className="p-4 border border-gray-200 rounded-lg"><h3 className="font-semibold text-[#16a085]">Right to Data Portability</h3><p className="mt-1 text-sm">Request transfer of your data to another organization.</p></div>
                  <div className="p-4 border border-gray-200 rounded-lg"><h3 className="font-semibold text-[#16a085]">Right to Object</h3><p className="mt-1 text-sm">Object to our processing of your personal data.</p></div>
                </div>
                <p className="mt-6">To exercise any of these rights, please contact us using the information in the "Contact Us" section below.</p>
              </section>

              <section id="data-security" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">9. Data Security</h2>
                <p>We implement appropriate technical and organizational measures to protect the security of your personal information. These include:</p>
                <ul className="mt-4 space-y-3 list-disc list-inside">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and testing</li>
                  <li>Access controls and authentication procedures</li>
                  <li>Incident response and breach notification procedures</li>
                </ul>
                <p className="mt-4">However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.</p>
              </section>

              <section id="cookies" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">10. Cookies and Tracking Technologies</h2>
                <p>We use cookies and similar tracking technologies to track activity on our platform and hold certain information. Cookies are files with small amounts of data which may include an anonymous unique identifier.</p>
                <div className="mt-6 grid gap-6 md:grid-cols-3">
                  <div className="p-4 bg-gray-50 rounded-lg"><h3 className="font-semibold text-[#16a085]">Essential Cookies</h3><p className="mt-2 text-sm">Necessary for the website to function and cannot be switched off.</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><h3 className="font-semibold text-[#16a085]">Performance Cookies</h3><p className="mt-2 text-sm">Help us understand how visitors interact with our website.</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><h3 className="font-semibold text-[#16a085]">Marketing Cookies</h3><p className="mt-2 text-sm">Used to track visitors across websites for advertising purposes.</p></div>
                </div>
                <p className="mt-6">You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.</p>
              </section>

              <section id="childrens-privacy" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">11. Children's Privacy</h2>
                <p>Our services are not intended for use by children under the age of 16 ("Children"). We do not knowingly collect personally identifiable information from children under 16. If you are a parent or guardian and you are aware that your child has provided us with personal data, please contact us.</p>
                <p className="mt-4">If we become aware that we have collected personal data from children without verification of parental consent, we take steps to remove that information from our servers.</p>
              </section>

              <section id="policy-updates" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">12. Changes to This Privacy Policy</h2>
                <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "effective date" at the top of this policy.</p>
                <p className="mt-4">You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
              </section>

              <section id="contact-us" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">13. Contact Us</h2>
                <p>If you have any questions about this Privacy Policy, please contact our Data Protection Officer:</p>
                <div className="mt-6 space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0"><svg className="h-6 w-6 text-[#16a085]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>
                    <div className="ml-3"><p className="text-sm font-medium text-gray-900">Email</p><a href="mailto:contact@shigjeta.com" className="text-sm text-[#16a085] hover:underline">contact@shigjeta.com</a></div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0"><svg className="h-6 w-6 text-[#16a085]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div>
                    <div className="ml-3"><p className="text-sm font-medium text-gray-900">Address</p><p className="text-sm text-gray-700">Void Support System HQ, Kacanik, Kosovo</p></div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}